import { useState } from 'react';
import {
  useDeletePtoPprTkDraftMutation,
  useLazyExportPtoPprTkDraftQuery,
  useRestartPtoPprTkDraftGenerationMutation,
  useReviewPtoPprTkDraftMutation,
  type PtoPprTkDraft,
} from '@shared/api';
import { errorText, safeFileName, saveBlob } from '../kfo/ui';
import { docShort } from './model';

/** Подтверждение перезапуска готового документа. */
export const CONFIRM_RESTART =
  'Перезапустить генерацию? Разделы, которые вы не правили, будут написаны заново.';

/** Подтверждение перезапуска зависшей генерации (хода работы нет больше 30 мин). */
export const CONFIRM_RESTART_STALLED =
  'Похоже, генерация зависла: хода работы нет больше 30 минут. Перезапустить? ' +
  'Разделы, которые вы не правили, будут написаны заново.';

/**
 * Действия над черновиком rd: выгрузка DOCX, повторный аудит, перезапуск
 * генерации, удаление. Ошибка — текст сервера по-русски (409 «генерация уже
 * идёт», «чистовой — только после утверждения»).
 */
export function useRdActions(draft: PtoPprTkDraft) {
  const [error, setError] = useState('');
  const [exportDocx, { isFetching: exporting }] = useLazyExportPtoPprTkDraftQuery();
  const [review, { isLoading: reviewing }] = useReviewPtoPprTkDraftMutation();
  const [restart, { isLoading: restarting }] = useRestartPtoPprTkDraftGenerationMutation();
  const [remove, { isLoading: deleting }] = useDeletePtoPprTkDraftMutation();

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError('');
    try {
      await action();
    } catch (e) {
      setError(errorText(e, fallback));
    }
  }

  return {
    error,
    exporting,
    reviewing,
    restarting,
    deleting,

    /** Черновик — со служебными пометками; чистовой — только у утверждённого. */
    exportDocx: (clean: boolean) =>
      run(async () => {
        const blob = await exportDocx({ id: draft.id, clean }).unwrap();
        const base = safeFileName(draft.title || docShort(draft.kind));
        saveBlob(blob, `${base}${clean ? '' : '_черновик'}.docx`);
      }, 'Не удалось выгрузить DOCX.'),

    review: () => run(() => review(draft.id).unwrap(), 'Не удалось запустить проверку.'),

    /**
     * Перезапуск всей генерации: после сбоя, у готового документа и у зависшей
     * (409 сервера — текстом). `confirmText` — подтверждение, null — без него.
     */
    restart: (confirmText: string | null) => {
      if (confirmText && !window.confirm(confirmText)) return Promise.resolve();
      return run(() => restart(draft.id).unwrap(), 'Не удалось перезапустить генерацию.');
    },

    remove: () => {
      if (!window.confirm(`Удалить черновик «${draft.title}»?`)) return Promise.resolve();
      return run(() => remove(draft.id).unwrap(), 'Не удалось удалить черновик.');
    },
  };
}

export type RdActions = ReturnType<typeof useRdActions>;
